import React from 'react';
import { Upload, Button, message, Row, Col, Typography, Progress } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import CustomModal from '../../../shared/util/AntModal/CustomModal';
import AlertModal from '../../../shared/util/AntModal/AlertModal';
import { UploadFile } from 'antd/es/upload/interface';
import BatchProvider from '../../../shared/provider/BatchProvider';
import { useSelection } from '../../../shared/context/selection/selectionProvider';

const { Text } = Typography;

interface ImportModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

const ImportModal: React.FC<ImportModalProps> = ({ visible, onCancel, onSuccess }) => {
  const [csvFiles, setCsvFiles] = React.useState<{
    userFile: UploadFile | null;
    dimensionFile: UploadFile | null;
    categoryDimensionFile: UploadFile | null;
    categoryFile: UploadFile | null;
    challengeFile: UploadFile | null;
    stageFile: UploadFile | null;
    ideaFile: UploadFile | null;
  }>({
    userFile: null,
    dimensionFile: null,
    categoryDimensionFile: null,
    categoryFile: null,
    challengeFile: null,
    stageFile: null,
    ideaFile: null,
  });

  const [errors, setErrors] = React.useState<{
    userFile?: string;
    dimensionFile?: string;
    categoryDimensionFile?: string;
    categoryFile?: string;
    challengeFile?: string;
    stageFile?: string;
    ideaFile?: string;
  }>({});

  const { tenantName } = useSelection();

  const [importSuccessModalVisible, setImportSuccessModalVisible] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [batchId, setBatchId] = React.useState<number | null>(null);

  const handleFileChange = (file: UploadFile, fileType: keyof typeof csvFiles) => {
    if (file.size && file.size > 2 * 1024 * 1024) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [fileType]: "O arquivo deve ser menor que 2MB.",
      }));
      setCsvFiles(prevState => ({
        ...prevState,
        [fileType]: null,
      }));
    } else {
      setErrors(prevErrors => ({
        ...prevErrors,
        [fileType]: undefined,
      }));
      setCsvFiles(prevState => ({
        ...prevState,
        [fileType]: file,
      }));
    }
  };

  const handleImport = async () => {
    const { userFile, dimensionFile, categoryDimensionFile, categoryFile, challengeFile, stageFile, ideaFile } = csvFiles;
  
    if (!userFile || !dimensionFile || !categoryDimensionFile || !categoryFile || !challengeFile || !stageFile || !ideaFile) {
      setErrors({
        userFile: !userFile ? "Todos os arquivos CSV são obrigatórios." : undefined,
        dimensionFile: !dimensionFile ? "Todos os arquivos CSV são obrigatórios." : undefined,
        categoryDimensionFile: !categoryDimensionFile ? "Todos os arquivos CSV são obrigatórios." : undefined,
        categoryFile: !categoryFile ? "Todos os arquivos CSV são obrigatórios." : undefined,
        challengeFile: !challengeFile ? "Todos os arquivos CSV são obrigatórios." : undefined,
        stageFile: !stageFile ? "Todos os arquivos CSV são obrigatórios." : undefined,
        ideaFile: !ideaFile ? "Todos os arquivos CSV são obrigatórios." : undefined,
      });
      return;
    }
  
    const createUploadFile = (file: UploadFile): UploadFile & { originFileObj: File } => ({
      ...file,
      uid: file.uid || String(Date.now()), // Garante que o uid esteja presente
      originFileObj: file.originFileObj || (file as any), // Se originFileObj não existir, usa o próprio objeto
    });
  
    const batchProvider = new BatchProvider();
    try {
      const batchId = await batchProvider.migrateData({
        userFile: createUploadFile(userFile),
        dimensionFile: createUploadFile(dimensionFile),
        categoryDimensionFile: createUploadFile(categoryDimensionFile),
        categoryFile: createUploadFile(categoryFile),
        challengeFile: createUploadFile(challengeFile),
        stageFile: createUploadFile(stageFile),
        ideaFile: createUploadFile(ideaFile),
      });
      setBatchId(batchId);
      checkBatchStatus(batchId);
    } catch (error) {
      message.error("Erro ao importar dados.");
    }
  };

  const checkBatchStatus = async (batchId: number) => {
    const batchProvider = new BatchProvider();
    const intervalId = setInterval(async () => {
      try {
        const status = await batchProvider.getBatchStatus(batchId);
        if (status === "CONCLUIDO") {
          clearInterval(intervalId);
          setProgress(100);
          setImportSuccessModalVisible(true);
          onSuccess();
        } else {
          setProgress((previous) => Math.min(previous + 10, 90));
        }
      } catch (error) {
        console.error("Erro ao obter status do lote:", error);
        clearInterval(intervalId);
      }
    }, 3500);
  };

  const handleCancel = () => {
    setCsvFiles({
      userFile: null,
      dimensionFile: null,
      categoryDimensionFile: null,
      categoryFile: null,
      challengeFile: null,
      stageFile: null,
      ideaFile: null,
    });
    setErrors({});
    setProgress(0);
    setBatchId(null);
    setImportSuccessModalVisible(false);
    onCancel();
  };

  return (
    <>
      <CustomModal
        title={`Importar dados para o Tenant ${tenantName}`}
        visible={visible}
        onCancel={handleCancel}
        onConfirm={handleImport}
        showSuccess={false}
        showWarning={false}
        confirmText="Enviar"
        cancelText="Cancelar"
      >
        <Text type="secondary">Tamanho máximo por arquivo: 2MB. Apenas arquivos CSV são permitidos.</Text>
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={12} style={{ display: 'flex', flexDirection: 'column' }}>
            <Text strong>Usuários</Text>
            <Upload
              beforeUpload={(file) => {
                handleFileChange(file, "userFile");
                return false;
              }}
              maxCount={1}
              accept=".csv"
              fileList={csvFiles.userFile ? [csvFiles.userFile] : []}
            >
              <Button icon={<UploadOutlined />}>Selecionar Arquivo</Button>
            </Upload>
            {errors.userFile && <Text type="danger">{errors.userFile}</Text>}
          </Col>
          <Col span={12} style={{ display: 'flex', flexDirection: 'column' }}>
            <Text strong>Dimensões</Text>
            <Upload
              beforeUpload={(file) => {
                handleFileChange(file, "dimensionFile");
                return false;
              }}
              maxCount={1}
              accept=".csv"
              fileList={csvFiles.dimensionFile ? [csvFiles.dimensionFile] : []}
            >
              <Button icon={<UploadOutlined />}>Selecionar Arquivo</Button>
            </Upload>
            {errors.dimensionFile && <Text type="danger">{errors.dimensionFile}</Text>}
          </Col>
          <Col span={12} style={{ display: 'flex', flexDirection: 'column' }}>
            <Text strong>Critérios de Dimensões</Text>
            <Upload
              beforeUpload={(file) => {
                handleFileChange(file, "categoryDimensionFile");
                return false;
              }}
              maxCount={1}
              accept=".csv"
              fileList={csvFiles.categoryDimensionFile ? [csvFiles.categoryDimensionFile] : []}
            >
              <Button icon={<UploadOutlined />}>Selecionar Arquivo</Button>
            </Upload>
            {errors.categoryDimensionFile && <Text type="danger">{errors.categoryDimensionFile}</Text>}
          </Col>
          <Col span={12} style={{ display: 'flex', flexDirection: 'column' }}>
            <Text strong>Categorias</Text>
            <Upload
              beforeUpload={(file) => {
                handleFileChange(file, "categoryFile");
                return false;
              }}
              maxCount={1}
              accept=".csv"
              fileList={csvFiles.categoryFile ? [csvFiles.categoryFile] : []}
            >
              <Button icon={<UploadOutlined />}>Selecionar Arquivo</Button>
            </Upload>
            {errors.categoryFile && <Text type="danger">{errors.categoryFile}</Text>}
          </Col>
          <Col span={12} style={{ display: 'flex', flexDirection: 'column' }}>
            <Text strong>Desafios</Text>
            <Upload
              beforeUpload={(file) => {
                handleFileChange(file, "challengeFile");
                return false;
              }}
              maxCount={1}
              accept=".csv"
              fileList={csvFiles.challengeFile ? [csvFiles.challengeFile] : []}
            >
              <Button icon={<UploadOutlined />}>Selecionar Arquivo</Button>
            </Upload>
            {errors.challengeFile && <Text type="danger">{errors.challengeFile}</Text>}
          </Col>
          <Col span={12} style={{ display: 'flex', flexDirection: 'column' }}>
            <Text strong>Fases</Text>
            <Upload
              beforeUpload={(file) => {
                handleFileChange(file, "stageFile");
                return false;
              }}
              maxCount={1}
              accept=".csv"
              fileList={csvFiles.stageFile ? [csvFiles.stageFile] : []}
            >
              <Button icon={<UploadOutlined />}>Selecionar Arquivo</Button>
            </Upload>
            {errors.stageFile && <Text type="danger">{errors.stageFile}</Text>}
          </Col>
          <Col span={12} style={{ display: 'flex', flexDirection: 'column' }}>
            <Text strong>Ideias</Text>
            <Upload
              beforeUpload={(file) => {
                handleFileChange(file, "ideaFile");
                return false;
              }}
              maxCount={1}
              accept=".csv"
              fileList={csvFiles.ideaFile ? [csvFiles.ideaFile] : []}
            >
              <Button icon={<UploadOutlined />}>Selecionar Arquivo</Button>
            </Upload>
            {errors.ideaFile && <Text type="danger">{errors.ideaFile}</Text>}
          </Col>
        </Row>
        {batchId && (
          <Progress percent={progress} />
        )}
      </CustomModal>

      <AlertModal
        title="Sucesso"
        visible={importSuccessModalVisible}
        onCancel={handleCancel}
        onConfirm={handleCancel}
        confirmText="Fechar"
        showCancelButton={false}
      >
        Dados importados com sucesso!
      </AlertModal>
    </>
  );
};

export default ImportModal;
